import * as React from 'react';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getBrandIncludedBaggage, getGdsServicesBySegments, getIsCancelled } from '../../../Checkout/store/order/selectors';
import { BaggageType, OrderAdditionalServiceGdsServiceServiceType } from '@websky/graphql';
import { useTheme } from '../../../theme';
import BaggageCard from './Service/Baggage/Baggage';
import Meal from './Service/Meal/Meal';
import Seat from './Service/Seat/Seat';
import { Button } from '../../../index';
import { useTranslation } from 'react-i18next';
import DesktopFlightModalContentSelected from '../../../DesktopFlight/components/DesktopFlightModalContentSelected/DesktopFlightModalContentSelected';
import { getFlightWithSegmentGroup } from '../../../Checkout/components/Checkout/SelectedFlight/utils';
import Modal from '../../../Modal';
import { useToggleable } from '../../../hooks';
import cn from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { MOBILE_MIN_WIDTH } from '../../../utils';
const SERVICES_ORDER = {
    CarryOn: 4,
    [OrderAdditionalServiceGdsServiceServiceType.Baggage]: 3,
    [OrderAdditionalServiceGdsServiceServiceType.Seat]: 2,
    [OrderAdditionalServiceGdsServiceServiceType.Meal]: 1
};
const SegmentServices = ({ segmentId, flight, orderId }) => {
    var _a, _b, _c;
    const { t } = useTranslation('FlightInfoCard');
    const { isOpen, open, close } = useToggleable(false);
    const { DesktopFlightPopup: popupCss } = useTheme('DesktopFlight');
    const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });
    const cancelled = useSelector(getIsCancelled);
    const services = useSelector(getGdsServicesBySegments), brandIncludedBaggage = useSelector(getBrandIncludedBaggage), segmentGroup = flight.segmentGroups.find(group => group.segments.some(segment => segment.id === segmentId));
    const segmentServices = services.find(service => service.segmentId === segmentId);
    const includedSegmentServices = brandIncludedBaggage.filter(({ baggage }) => baggage.includedSegments.includes(segmentId));
    const theme = useTheme('FlightInfoCard').SegmentServices;
    const flightWithSegmentGroup = getFlightWithSegmentGroup(flight, flight.segmentGroups.find(group => group.segments.some(segment => segment.id === segmentId)));
    const separateSelectedBaggageByRfics = (baggageServiceGroup) => {
        const groups = [];
        const baggageRficsMap = new Map();
        baggageServiceGroup.selectedServices.forEach(baggage => {
            const baggageService = baggage.service;
            if (baggageRficsMap.has(baggageService.rfics)) {
                baggageRficsMap.set(baggageService.rfics, Object.assign(Object.assign({}, baggageRficsMap.get(baggageService.rfics)), { selectedServices: [
                        ...baggageRficsMap.get(baggageService.rfics).selectedServices,
                        Object.assign({}, baggage)
                    ] }));
            }
            else {
                baggageRficsMap.set(baggageService.rfics, Object.assign(Object.assign({}, baggageServiceGroup), { selectedServices: [
                        Object.assign({}, baggage)
                    ], type: baggageService.type === BaggageType.CarryOn
                        ? 'CarryOn'
                        : OrderAdditionalServiceGdsServiceServiceType.Baggage }));
            }
        });
        baggageRficsMap.forEach(baggage => {
            groups.push(baggage);
        });
        return groups.length ? groups : [baggageServiceGroup];
    };
    const filteredServices = React.useMemo(() => {
        const services = new Map();
        includedSegmentServices.forEach(({ baggage, count }) => services.set(baggage.rfics, {
            type: baggage.type === BaggageType.CarryOn
                ? 'CarryOn'
                : OrderAdditionalServiceGdsServiceServiceType.Baggage,
            services: [],
            selectedServices: [
                {
                    count,
                    service: Object.assign(Object.assign({}, baggage), { _serviceType: 'gdsService' }),
                    type: OrderAdditionalServiceGdsServiceServiceType.Baggage
                }
            ],
            canAddService: baggage.canBeAdded
        }));
        segmentServices.services
            .filter(service => {
            return (SERVICES_ORDER.hasOwnProperty(service.type) &&
                (isMobile ||
                    service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage ||
                    !!service.selectedServices.length));
        })
            .forEach(service => {
            var _a, _b;
            const serviceRfisc = (_b = (_a = service.selectedServices[0]) === null || _a === void 0 ? void 0 : _a.service) === null || _b === void 0 ? void 0 : _b.rfics;
            if (!services.has(serviceRfisc)) {
                if (service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage) {
                    separateSelectedBaggageByRfics(service).forEach(service => services.set(serviceRfisc, service));
                }
                else {
                    services.set(serviceRfisc, service);
                }
            }
        });
        return Array.from(services.values()).sort((a, b) => SERVICES_ORDER[b.type] - SERVICES_ORDER[a.type]);
    }, [segmentServices, isMobile]);
    const renderService = useCallback((service) => {
        const type = service.type;
        switch (type) {
            case 'CarryOn':
            case OrderAdditionalServiceGdsServiceServiceType.Baggage:
                return React.createElement(BaggageCard, { services: service, key: type });
            case OrderAdditionalServiceGdsServiceServiceType.Meal:
                return React.createElement(Meal, { services: service, key: type });
            case OrderAdditionalServiceGdsServiceServiceType.Seat:
                return React.createElement(Seat, { services: service, key: type });
            default:
                return null;
        }
    }, []);
    const servicesContainerStyles = !isMobile
        ? {
            gridTemplateColumns: new Array(filteredServices.length > 3 ? 3 : filteredServices.length)
                .fill('1fr')
                .join(' ')
        }
        : null;
    return (React.createElement("div", { className: theme.container },
        React.createElement("div", { className: theme.wrapper },
            React.createElement("div", { className: theme.fareName },
                t('Fare'),
                " ", (_a = segmentGroup === null || segmentGroup === void 0 ? void 0 : segmentGroup.fareFamily) === null || _a === void 0 ? void 0 :
                _a.title),
            React.createElement(Button, { variant: "text", onClick: open }, t('Details'))),
        !cancelled && (React.createElement("div", { className: theme.services, style: servicesContainerStyles }, filteredServices.map(renderService))),
        React.createElement(Modal, { scroll: isMobile ? 'body' : 'paper', classes: {
                paper: cn(popupCss.paper, popupCss.paper_fullScreen),
                scrollBody: popupCss.scrollBody
            }, closeButtonRenderer: () => null, open: isOpen, onClose: close, fullScreen: true },
            React.createElement(DesktopFlightModalContentSelected, { orderId: orderId, flightId: flightWithSegmentGroup.id, flight: flightWithSegmentGroup, onClose: close, lastSelectedFare: (_c = (_b = flightWithSegmentGroup.pricesForFareGroups) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.fareFamily }))));
};
export default SegmentServices;
